<template>
  <div>
    <div class="row">
      <div class="col-md-12">
        <KTCodePreview v-bind:title="'Chốt sổ hoa hồng'">
          <template v-slot:preview>
            <!-- Chọn kỳ + phạm vi.
                 Hàng 1 cố định một dòng (ngành, kỳ, nút) nên nút không nhảy chỗ;
                 ô cửa hàng nằm riêng hàng 2 vì chọn nhiều thì ô cao dần lên. -->
            <b-form class="settlement-filter mb-5">
              <b-row class="align-items-end">
                <b-col md="2">
                  <label class="form-label">Ngành</label>
                  <b-form-select
                    size="sm"
                    v-model="filter.type"
                    :options="typeOptions"
                    value-field="id"
                    text-field="name"
                    @change="onTypeChange"
                  ></b-form-select>
                </b-col>
                <b-col md="3" v-if="!isStore">
                  <label class="form-label">Bộ phận</label>
                  <b-form-select
                    size="sm"
                    v-model="filter.departmentId"
                    :options="departments"
                    value-field="id"
                    text-field="name"
                  >
                    <template v-slot:first>
                      <b-form-select-option :value="null"
                        >-- Chọn bộ phận --</b-form-select-option
                      >
                    </template>
                  </b-form-select>
                </b-col>
                <b-col md="2">
                  <label class="form-label">Từ ngày</label>
                  <date-picker
                    placeholder="Từ ngày"
                    class="form-control form-control-sm"
                    :config="dpConfigs.date"
                    v-model="filter.fromDate"
                  ></date-picker>
                </b-col>
                <b-col md="2">
                  <label class="form-label">Đến ngày</label>
                  <date-picker
                    placeholder="Đến ngày"
                    class="form-control form-control-sm"
                    :config="dpConfigs.date"
                    v-model="filter.toDate"
                  ></date-picker>
                </b-col>
                <b-col md="3">
                  <b-button
                    variant="primary"
                    size="sm"
                    class="px-5"
                    :disabled="onLoading"
                    @click="checkStatus"
                  >
                    <i class="fas fa-search mr-1"></i>Kiểm tra kỳ
                  </b-button>
                </b-col>
              </b-row>
              <b-row v-if="isStore" class="mt-3">
                <b-col md="12">
                  <label class="form-label">
                    Cửa hàng
                    <span v-if="filter.stores.length" class="text-dark"
                      >({{ filter.stores.length }} đã chọn)</span
                    >
                    <!-- Chọn nhiều cửa hàng để chốt một lượt -->
                    <b-link class="ml-3" @click="selectAllStores">Chọn tất cả</b-link>
                    <b-link
                      v-if="filter.stores.length"
                      class="ml-3"
                      @click="clearStores"
                      >Bỏ chọn</b-link
                    >
                  </label>
                  <vue-multi-select
                    v-model="filter.stores"
                    :options="stores"
                    placeholder="Chọn một hoặc nhiều cửa hàng"
                    option-label="name"
                  ></vue-multi-select>
                </b-col>
              </b-row>
            </b-form>

            <!-- Trạng thái kỳ -->
            <div v-if="status" class="mb-5">
              <div
                class="status-box"
                :class="status.isSettled ? 'status-box--locked' : 'status-box--open'"
              >
                <div>
                  <div class="status-box__title">
                    <i
                      class="mr-2"
                      :class="status.isSettled ? 'fas fa-lock' : 'fas fa-lock-open'"
                    ></i>
                    {{ status.isSettled ? 'Kỳ đã chốt sổ' : 'Kỳ chưa chốt' }}
                  </div>
                  <div class="status-box__meta" v-if="status.isSettled">
                    {{ status.settlement.employeeCount }} nhân sự ·
                    Tổng
                    <strong>{{ displayAmount(status.settlement.totalAmount) }}</strong>
                    · Chốt lúc
                    {{ displayDate(status.settlement.settledAt) }}
                    <span v-if="status.settlement.settledByName">
                      bởi {{ status.settlement.settledByName }}
                    </span>
                  </div>
                  <div class="status-box__meta" v-else>
                    Kỳ này chưa được chốt — dữ liệu vẫn sửa được bình thường.
                  </div>
                </div>
                <div>
                  <b-button
                    v-if="!status.isSettled"
                    variant="primary"
                    size="sm"
                    :disabled="isWorking"
                    @click="confirmSettle"
                  >
                    <i class="fas fa-lock mr-1"></i>Chốt sổ
                  </b-button>
                  <template v-else>
                    <b-button
                      variant="outline-primary"
                      size="sm"
                      class="mr-2"
                      @click="openStatement(status.settlement.id)"
                    >
                      <i class="fas fa-eye mr-1"></i>Xem chi tiết
                    </b-button>
                    <b-button
                      variant="primary"
                      size="sm"
                      class="mr-2"
                      :disabled="isWorking"
                      @click="confirmSettle"
                    >
                      <i class="fas fa-redo mr-1"></i>Chốt lại
                    </b-button>
                    <b-button
                      variant="danger"
                      size="sm"
                      :disabled="isWorking"
                      @click="confirmRevoke(status.settlement.id)"
                    >
                      <i class="fas fa-lock-open mr-1"></i>Huỷ chốt
                    </b-button>
                  </template>
                </div>
              </div>
            </div>

            <!-- Xem trước: đúng số liệu sẽ được lưu khi bấm Chốt sổ / Chốt lại -->
            <div v-if="status" class="mb-6">
              <p class="font-weight-bold mb-2">
                {{
                  status.isSettled
                    ? 'Số liệu hiện tại của kỳ (sẽ ghi đè nếu chốt lại):'
                    : 'Số liệu sẽ chốt:'
                }}
              </p>
              <vcl-table v-if="previewLoading" :columns="6"></vcl-table>
              <template v-else-if="preview">
                <b-alert
                  v-if="status.isSettled && previewDiff !== 0"
                  show
                  variant="warning"
                  class="py-2"
                >
                  Số hiện tại lệch
                  <strong>{{ displayAmount(previewDiff) }}</strong> so với bản đã
                  chốt ({{ displayAmount(status.settlement.totalAmount) }}).
                </b-alert>
                <SettlementPreview :preview="preview" />
              </template>
            </div>

            <!-- Chốt NHIỀU cửa hàng: mỗi cửa hàng một dòng, xem số rồi chốt một lượt -->
            <div v-if="batchRows.length" class="mb-6">
              <div class="d-flex justify-content-between align-items-center mb-2">
                <p class="font-weight-bold mb-0">
                  {{ batchRows.length }} cửa hàng — kỳ {{ filter.fromDate }} —
                  {{ filter.toDate }}
                  <span v-if="batchLoadingCount" class="text-muted font-weight-normal">
                    · đang tải số liệu {{ batchRows.length - batchLoadingCount }}/{{
                      batchRows.length
                    }}
                  </span>
                </p>
                <b-button
                  variant="primary"
                  size="sm"
                  :disabled="isWorking || !batchCheckedRows.length || batchLoadingCount > 0"
                  @click="confirmSettleBatch"
                >
                  <i class="fas fa-lock mr-1"></i>
                  {{
                    isWorking
                      ? `Đang chốt ${batchDoneCount}/${batchTargetCount}...`
                      : `Chốt ${batchCheckedRows.length} cửa hàng đã chọn`
                  }}
                </b-button>
              </div>
              <b-table
                class="table-bordered table-hover"
                :fields="batchFields"
                :items="batchRows"
                small
              >
                <template v-slot:head(checked)>
                  <b-form-checkbox
                    :checked="isBatchAllChecked"
                    :disabled="isWorking"
                    @change="toggleBatchAll"
                  ></b-form-checkbox>
                </template>
                <template v-slot:cell(checked)="row">
                  <b-form-checkbox
                    v-model="row.item.checked"
                    :disabled="isWorking"
                  ></b-form-checkbox>
                </template>
                <template v-slot:cell(status)="row">
                  <b-badge v-if="row.item.isSettled" variant="warning">Đã chốt</b-badge>
                  <b-badge v-else variant="light">Chưa chốt</b-badge>
                  <div v-if="row.item.isSettled" class="text-muted small mt-1">
                    {{ displayAmount(row.item.settledTotal) }}
                  </div>
                </template>
                <template v-slot:cell(employeeCount)="row">
                  <b-spinner v-if="row.item.loading" small variant="primary"></b-spinner>
                  <template v-else>{{ row.item.employeeCount }}</template>
                </template>
                <template v-slot:cell(adhocAmount)="row">
                  <template v-if="!row.item.loading">{{
                    displayAmount(row.item.adhocAmount)
                  }}</template>
                </template>
                <template v-slot:cell(totalAmount)="row">
                  <span
                    v-if="!row.item.loading"
                    class="font-weight-bold"
                    :class="amountClass(row.item.totalAmount)"
                    >{{ displayAmount(row.item.totalAmount) }}</span
                  >
                </template>
                <template v-slot:cell(result)="row">
                  <b-spinner
                    v-if="row.item.settling"
                    small
                    variant="primary"
                  ></b-spinner>
                  <span v-else-if="row.item.error" class="text-danger">{{
                    row.item.error
                  }}</span>
                  <span v-else-if="row.item.done" class="text-success">
                    <i class="fas fa-check-circle mr-1"></i>Đã chốt
                  </span>
                  <span
                    v-else-if="row.item.isSettled && row.item.diff !== 0 && !row.item.loading"
                    class="text-warning"
                  >
                    Lệch {{ displayAmount(row.item.diff) }} so với bản đã chốt
                  </span>
                </template>
                <!-- Đã chốt: mở chi tiết bản chốt. Chưa chốt: mở modal số liệu sẽ chốt của cửa hàng -->
                <template v-slot:cell(actions)="row">
                  <b-button
                    v-if="row.item.settlementId"
                    variant="link"
                    size="sm"
                    class="p-0 settlement-link"
                    @click="openStatement(row.item.settlementId)"
                  >
                    Xem chi tiết
                  </b-button>
                  <b-button
                    v-else
                    variant="link"
                    size="sm"
                    class="p-0 settlement-link"
                    :disabled="row.item.loading"
                    @click="openStorePreview(row.item)"
                  >
                    Xem chi tiết
                  </b-button>
                </template>
              </b-table>
            </div>

            <!-- Lịch sử các kỳ đã chốt -->
            <p class="font-weight-bold mb-2">Lịch sử chốt sổ:</p>
            <b-table
              class="table-bordered table-hover"
              :fields="historyFields"
              :items="history"
              :busy="onLoadingHistory"
              small
              show-empty
              empty-text="Chưa có kỳ nào được chốt"
            >
              <template v-slot:table-busy>
                <vcl-table :speed="5" :animate="true" :columns="6"></vcl-table>
              </template>
              <template v-slot:cell(scope)="row">
                {{ row.item.type === 3 ? 'CR' : 'Cửa hàng' }}
                <span class="text-muted">
                  {{ row.item.storeId || row.item.departmentId }}
                </span>
              </template>
              <template v-slot:cell(period)="row">
                {{ displayDate(row.item.fromDate) }} —
                {{ displayDate(row.item.toDate) }}
              </template>
              <template v-slot:cell(totalAmount)="row">
                <span :class="amountClass(row.item.totalAmount)">
                  {{ displayAmount(row.item.totalAmount) }}
                </span>
              </template>
              <template v-slot:cell(settledAt)="row">
                {{ displayDate(row.item.settledAt) }}
              </template>
              <template v-slot:cell(actions)="row">
                <b-button
                  variant="link"
                  size="sm"
                  class="p-0 settlement-link"
                  @click="openStatement(row.item.id)"
                >
                  Xem báo cáo
                </b-button>
              </template>
            </b-table>
          </template>
        </KTCodePreview>
      </div>
    </div>

    <!-- Số liệu sẽ chốt của MỘT cửa hàng trong bảng chốt hàng loạt -->
    <b-modal
      v-model="storePreviewVisible"
      :title="'Số liệu sẽ chốt — ' + storePreviewName"
      size="xl"
      ok-only
      ok-title="Đóng"
      ok-variant="secondary"
    >
      <vcl-table v-if="storePreviewLoading" :columns="6"></vcl-table>
      <SettlementPreview v-else-if="storePreview" :preview="storePreview" />
    </b-modal>

    <!-- Bảng kê trình ký -->
    <b-modal
      v-model="statementVisible"
      title="Chi tiết chốt hoa hồng"
      size="xl"
      ok-only
      ok-title="Đóng"
    >
      <vcl-table v-if="statementLoading" :columns="8"></vcl-table>
      <div v-else-if="statement">
        <div class="statement-head mb-3">
          <div>
            Kỳ:
            <strong>
              {{ displayDate(statement.header.fromDate) }} —
              {{ displayDate(statement.header.toDate) }}
            </strong>
          </div>
          <div v-if="statement.header.storeName">
            Cửa hàng: <strong>{{ statement.header.storeName }}</strong>
          </div>
          <div v-else-if="statement.header.departmentId">
            Bộ phận: <strong>{{ statement.header.departmentId }}</strong>
          </div>
          <div>
            Ngày chốt:
            <strong>{{ displayDate(statement.header.settledAt) }}</strong>
            <span v-if="statement.header.settledByName">
              · Người chốt: <strong>{{ statement.header.settledByName }}</strong>
            </span>
          </div>
        </div>

        <b-alert v-if="statement.hiddenZeroCount" show variant="light" class="hint">
          {{ statement.hiddenZeroCount }} dòng có Tổng thưởng bằng 0 không đưa vào
          bảng kê (vẫn được lưu đầy đủ trong bảng chốt sổ).
        </b-alert>

        <b-table
          class="table-bordered table-hover"
          :fields="statementFields"
          :items="statement.rows"
          small
          show-empty
          empty-text="Không có dòng nào"
        >
          <template v-slot:cell(count)="row">{{ row.index + 1 }}</template>
          <template v-slot:cell(commissionAmount)="row">
            {{ displayAmount(row.item.commissionAmount) }}
          </template>
          <template v-slot:cell(adhocAmount)="row">
            {{ displayAmount(row.item.adhocAmount) }}
          </template>
          <template v-slot:cell(bonusAmount)="row">
            {{ displayAmount(row.item.bonusAmount) }}
          </template>
          <template v-slot:cell(minusAmount)="row">
            {{ displayAmount(row.item.minusAmount) }}
          </template>
          <template v-slot:cell(totalAmount)="row">
            <span class="font-weight-bold" :class="amountClass(row.item.totalAmount)">
              {{ displayAmount(row.item.totalAmount) }}
            </span>
          </template>
          <template v-slot:custom-foot>
            <tr class="statement-total">
              <td colspan="4" class="text-right">TỔNG CỘNG</td>
              <td class="text-right">
                {{ displayAmount(statement.totals.commissionAmount) }}
              </td>
              <td class="text-right">
                {{ displayAmount(statement.totals.adhocAmount) }}
              </td>
              <td class="text-right">
                {{ displayAmount(statement.totals.bonusAmount) }}
              </td>
              <td class="text-right">
                {{ displayAmount(statement.totals.minusAmount) }}
              </td>
              <td class="text-right" :class="amountClass(statement.totals.totalAmount)">
                {{ displayAmount(statement.totals.totalAmount) }}
              </td>
            </tr>
          </template>
        </b-table>
      </div>
    </b-modal>
  </div>
</template>

<script>
/**
 * CHỐT SỔ HOA HỒNG — SRS F-08 / §7.10.
 *
 * Chốt sổ là xác nhận TIỀN ĐÃ TRẢ cho kỳ. Chốt xong kỳ bị khoá: bút toán
 * cộng/trừ thuộc kỳ không sửa/xoá/thêm được. Muốn sửa: huỷ chốt → sửa → chốt lại.
 *
 * Bảng kê (để trình ký) BỎ dòng Tổng thưởng = 0 nhưng GIỮ dòng âm — khác bảng
 * chốt sổ vốn lưu hết. Số dòng bị ẩn được nói rõ để người đọc không thắc mắc.
 */
import KTCodePreview from '@/view/content/CodePreview.vue';
import VueMultiSelect from 'vue-simple-multi-select';
import SettlementPreview from './SettlementPreview.vue';
import datePicker from 'vue-bootstrap-datetimepicker';
import { SET_BREADCRUMB } from '@/core/services/store/modules/breadcrumbs.module';
import ApiService from '@/core/services/api.service';
import Swal from 'sweetalert2';
import moment from 'moment';
import { VclTable } from 'vue-content-loading';
import { makeToastSuccess, makeToastFaile } from '@/utils/common';
import commissionReportMixin, {
  DATE_INPUT_FORMAT,
  thStyle,
} from './commissionReport.mixin';

const TYPE_STORE = 1;
const TYPE_CR = 3;

export default {
  components: {
    KTCodePreview,
    VueMultiSelect,
    VclTable,
    datePicker,
    SettlementPreview,
  },
  mixins: [commissionReportMixin],
  data() {
    return {
      onLoading: false,
      onLoadingHistory: false,
      isWorking: false,
      status: null,
      // Số liệu xem trước của kỳ đang chọn — cùng nguồn (báo cáo) mà API chốt sổ sẽ lưu
      preview: null,
      previewLoading: false,
      // Modal xem chi tiết một cửa hàng chưa chốt trong bảng chốt hàng loạt
      storePreviewVisible: false,
      storePreviewLoading: false,
      storePreviewName: '',
      storePreview: null,
      history: [],
      stores: [],
      departments: [],
      // Chốt hàng loạt: mỗi cửa hàng một dòng { storeId, storeName, checked, loading, ... }
      batchRows: [],
      batchRunId: 0,
      batchTargetCount: 0,
      batchDoneCount: 0,
      batchFields: [
        { key: 'checked', label: '', thStyle: thStyle('4%'), tdClass: 'text-center' },
        { key: 'storeName', label: 'Cửa hàng', thStyle: thStyle('26%') },
        { key: 'status', label: 'Trạng thái kỳ', thStyle: thStyle('14%') },
        {
          key: 'employeeCount',
          label: 'Nhân sự',
          thStyle: thStyle('8%'),
          tdClass: 'text-center',
        },
        { key: 'adhocAmount', label: 'AdHoc', thStyle: thStyle('12%'), tdClass: 'text-right' },
        {
          key: 'totalAmount',
          label: 'Tổng thưởng sẽ chốt',
          thStyle: thStyle('14%'),
          tdClass: 'text-right',
        },
        { key: 'result', label: 'Kết quả', thStyle: thStyle('14%') },
        { key: 'actions', label: '', thStyle: thStyle('8%'), tdClass: 'text-center' },
      ],
      filter: {
        type: TYPE_STORE,
        // Một cửa hàng: xem trước chi tiết rồi chốt. Nhiều cửa hàng: bảng chốt hàng loạt.
        stores: [],
        // Cửa hàng nhận từ URL, chờ danh sách tải xong mới chọn được
        pendingStoreId: null,
        departmentId: null,
        fromDate: moment().startOf('month').format(DATE_INPUT_FORMAT),
        toDate: moment().endOf('month').format(DATE_INPUT_FORMAT),
      },
      typeOptions: [
        { id: TYPE_STORE, name: 'Cửa hàng' },
        { id: TYPE_CR, name: 'CR' },
      ],
      statementVisible: false,
      statementLoading: false,
      statement: null,

      historyFields: [
        { key: 'scope', label: 'Phạm vi', thStyle: thStyle('18%') },
        { key: 'period', label: 'Kỳ', thStyle: thStyle('24%') },
        {
          key: 'employeeCount',
          label: 'Số NV',
          thStyle: thStyle('10%'),
          tdClass: 'text-center',
        },
        {
          key: 'totalAmount',
          label: 'Tổng thưởng',
          thStyle: thStyle('18%'),
          tdClass: 'text-right',
        },
        { key: 'settledAt', label: 'Ngày chốt', thStyle: thStyle('20%') },
        { key: 'actions', label: '', thStyle: thStyle('10%') },
      ],
      statementFields: [
        {
          key: 'count',
          label: 'STT',
          thStyle: thStyle('5%'),
          tdClass: 'text-center',
        },
        { key: 'employeeCode', label: 'Mã NV', thStyle: thStyle('9%') },
        { key: 'employeeName', label: 'Tên nhân viên', thStyle: thStyle('20%') },
        { key: 'jobTitleName', label: 'Chức vụ', thStyle: thStyle('14%') },
        {
          key: 'commissionAmount',
          label: 'Hoa hồng',
          thStyle: thStyle('13%'),
          tdClass: 'text-right',
        },
        {
          key: 'adhocAmount',
          label: 'AdHoc',
          thStyle: thStyle('10%'),
          tdClass: 'text-right',
        },
        {
          key: 'bonusAmount',
          label: 'Cộng khác',
          thStyle: thStyle('10%'),
          tdClass: 'text-right',
        },
        {
          key: 'minusAmount',
          label: 'Trừ khác',
          thStyle: thStyle('9%'),
          tdClass: 'text-right',
        },
        {
          key: 'totalAmount',
          label: 'Tổng thưởng',
          thStyle: thStyle('10%'),
          tdClass: 'text-right',
        },
      ],
    };
  },
  computed: {
    /** Chênh lệch giữa số hiện tại và bản đã chốt (kỳ đã chốt mà dữ liệu đổi sau đó) */
    previewDiff() {
      if (!this.preview || !this.status || !this.status.isSettled) {
        return 0;
      }
      return (
        Number(this.preview.totals.totalAmount || 0) -
        Number(this.status.settlement.totalAmount || 0)
      );
    },
    /** Nhiều hơn một cửa hàng -> chế độ chốt hàng loạt */
    isBatch() {
      return this.isStore && this.filter.stores.length > 1;
    },
    singleStoreId() {
      return this.filter.stores.length === 1 ? this.filter.stores[0].id : null;
    },
    batchCheckedRows() {
      return this.batchRows.filter((row) => row.checked);
    },
    batchLoadingCount() {
      return this.batchRows.filter((row) => row.loading).length;
    },
    isBatchAllChecked() {
      return this.batchRows.length > 0 && this.batchRows.every((row) => row.checked);
    },
    isStore() {
      return Number(this.filter.type) === TYPE_STORE;
    },
  },
  created() {
    this.restoreFilterFromQuery();
    this.fetchStores();
    this.fetchDepartments();
    this.fetchHistory();
  },
  mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: 'Hoa hồng', route: '/commission/categories' },
      { title: 'Chốt sổ hoa hồng' },
    ]);
  },
  methods: {
    /**
     * Mở từ màn báo cáo: nhận sẵn loại, kỳ và cửa hàng / bộ phận qua URL.
     * Tên cửa hàng phải chờ danh sách tải xong mới gán được (xem fetchStores).
     */
    restoreFilterFromQuery() {
      const query = this.$route.query || {};
      if (query.type === 'cr') {
        this.filter.type = TYPE_CR;
      }
      if (query.fromDate) {
        this.filter.fromDate = query.fromDate;
      }
      if (query.toDate) {
        this.filter.toDate = query.toDate;
      }
      if (query.departmentId) {
        this.filter.departmentId = Number(query.departmentId);
        this.checkStatus();
      }
      if (query.storeId) {
        this.filter.pendingStoreId = Number(query.storeId);
      }
    },
    selectAllStores() {
      this.filter.stores = [...this.stores];
    },
    clearStores() {
      this.filter.stores = [];
    },
    onTypeChange() {
      this.status = null;
      this.preview = null;
      this.batchRows = [];
      this.filter.stores = [];
      this.filter.departmentId = null;
    },
    fetchStores() {
      ApiService.setHeader();
      ApiService.query('stores', { params: { limit: 1000, page: 1 } })
        .then(({ data }) => {
          this.stores = (data.data.list_store || []).map((store) => ({
            id: store.id,
            name: `${store.shortName} (${store.name})`,
            suggestionName: `${store.shortName} (${store.name})`,
          }));
          // Cửa hàng nhận từ URL: có danh sách rồi mới chọn được và kiểm tra kỳ
          const selected = this.stores.find(
            (store) => store.id === this.filter.pendingStoreId,
          );
          if (selected) {
            this.filter.pendingStoreId = null;
            this.filter.stores = [selected];
            this.checkStatus();
          }
        })
        .catch(() => makeToastFaile('Không tải được danh sách cửa hàng!'));
    },
    fetchDepartments() {
      ApiService.setHeader();
      ApiService.query('employees/departments')
        .then(({ data }) => {
          this.departments = data.data || [];
        })
        .catch(() => makeToastFaile('Không tải được danh sách bộ phận!'));
    },
    /** Tham số chung cho mọi lời gọi — gom một chỗ để không lệch giữa các hàm */
    buildParams() {
      return {
        type: this.isStore ? 'store' : 'cr',
        storeId: this.isStore ? this.singleStoreId : undefined,
        departmentId: this.isStore ? undefined : this.filter.departmentId,
        fromDate: this.toApiDate(this.filter.fromDate),
        toDate: this.toApiDate(this.filter.toDate),
      };
    },
    validate() {
      if (this.isStore && !this.filter.stores.length) {
        makeToastFaile('Vui lòng chọn cửa hàng');
        return false;
      }
      if (!this.isStore && !this.filter.departmentId) {
        makeToastFaile('Vui lòng chọn bộ phận');
        return false;
      }
      const message = this.validateDateRange(
        this.filter.fromDate,
        this.filter.toDate,
      );
      if (message) {
        makeToastFaile(message);
        return false;
      }
      return true;
    },

    checkStatus() {
      if (!this.validate()) {
        return;
      }
      this.status = null;
      this.preview = null;
      this.batchRows = [];
      if (this.isBatch) {
        this.loadBatch();
        return;
      }
      this.onLoading = true;
      ApiService.setHeader();
      ApiService.query('commission-settlements/status', { params: this.buildParams() })
        .then(({ data }) => {
          this.status = data.data;
          this.fetchPreview();
        })
        .catch((error) => {
          makeToastFaile(
            this.getErrorMessage(error, 'Không kiểm tra được trạng thái kỳ'),
          );
        })
        .finally(() => {
          this.onLoading = false;
        });
    },

    /* --------------------------- Chốt hàng loạt --------------------------- */
    /**
     * Nạp bảng chốt hàng loạt: trạng thái kỳ của mọi cửa hàng trong MỘT lời gọi, rồi
     * số liệu sẽ chốt của từng cửa hàng LẦN LƯỢT (mỗi cửa hàng là một lần chạy báo cáo).
     */
    async loadBatch() {
      const runId = this.batchRunId + 1;
      this.batchRunId = runId;
      const period = {
        fromDate: this.toApiDate(this.filter.fromDate),
        toDate: this.toApiDate(this.filter.toDate),
      };
      this.batchRows = this.filter.stores.map((store) => ({
        storeId: store.id,
        storeName: store.name,
        checked: false,
        loading: true,
        isSettled: false,
        settlementId: null,
        settledTotal: 0,
        employeeCount: 0,
        adhocAmount: 0,
        totalAmount: 0,
        diff: 0,
        settling: false,
        done: false,
        error: '',
      }));

      ApiService.setHeader();
      try {
        const { data } = await ApiService.query('commission-settlements/store-statuses', {
          params: {
            ...period,
            storeIds: this.batchRows.map((row) => row.storeId).join(','),
          },
        });
        if (runId !== this.batchRunId) {
          return;
        }
        (data.data.items || []).forEach((item) => {
          const row = this.batchRows.find((r) => r.storeId === item.storeId);
          if (row && item.isSettled) {
            row.isSettled = true;
            row.settlementId = item.settlement.id;
            row.settledTotal = item.settlement.totalAmount;
          }
        });
      } catch (error) {
        makeToastFaile(
          this.getErrorMessage(error, 'Không kiểm tra được trạng thái kỳ'),
        );
        this.batchRows = [];
        return;
      }

      for (const row of this.batchRows) {
        try {
          const { data } = await ApiService.query('commission-reports', {
            params: { type: 'store', storeId: row.storeId, ...period },
          });
          if (runId !== this.batchRunId) {
            return; // người dùng đã đổi bộ lọc — bỏ kết quả của lượt cũ
          }
          const totals = (data.data && data.data.totals) || {};
          row.employeeCount = totals.employeeCount || 0;
          row.adhocAmount = totals.adhocAmount || 0;
          row.totalAmount = totals.totalAmount || 0;
          row.diff = row.isSettled ? row.totalAmount - row.settledTotal : 0;
          // Mặc định chọn sẵn cửa hàng chưa chốt, hoặc đã chốt mà số đã đổi
          row.checked = !row.isSettled || row.diff !== 0;
        } catch (error) {
          if (runId !== this.batchRunId) {
            return;
          }
          row.error = this.getErrorMessage(error, 'Không tải được số liệu');
        }
        row.loading = false;
      }
    },
    /** Cửa hàng chưa chốt: xem từng nhân viên sẽ được chốt bao nhiêu, ngay trong modal */
    openStorePreview(row) {
      this.storePreviewName = row.storeName;
      this.storePreview = null;
      this.storePreviewLoading = true;
      this.storePreviewVisible = true;
      ApiService.setHeader();
      ApiService.query('commission-reports', {
        params: {
          type: 'store',
          storeId: row.storeId,
          fromDate: this.toApiDate(this.filter.fromDate),
          toDate: this.toApiDate(this.filter.toDate),
        },
      })
        .then(({ data }) => {
          const result = data.data || {};
          this.storePreview = {
            items: result.items || [],
            totals: result.totals || {},
          };
        })
        .catch((error) => {
          this.storePreviewVisible = false;
          makeToastFaile(
            this.getErrorMessage(error, 'Không tải được số liệu của cửa hàng'),
          );
        })
        .finally(() => {
          this.storePreviewLoading = false;
        });
    },
    toggleBatchAll(checked) {
      this.batchRows.forEach((row) => {
        row.checked = !!checked && !row.error;
      });
    },
    confirmSettleBatch() {
      const rows = this.batchCheckedRows;
      const resettleCount = rows.filter((row) => row.isSettled).length;
      Swal.fire({
        title: `Chốt sổ ${rows.length} cửa hàng?`,
        text: resettleCount
          ? `${resettleCount} cửa hàng đã chốt sẽ bị GHI ĐÈ bằng số liệu mới nhất. Sau khi chốt, bút toán cộng/trừ thuộc kỳ sẽ bị khoá.`
          : 'Sau khi chốt, bút toán cộng/trừ thuộc kỳ sẽ bị khoá.',
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#3699FF',
        cancelButtonColor: '#E5EAEE',
        confirmButtonText: 'Chốt sổ',
        cancelButtonText: 'Huỷ',
      }).then((result) => {
        if (result.value) {
          this.settleBatch(rows);
        }
      });
    },
    /** Chốt LẦN LƯỢT từng cửa hàng; một cửa hàng lỗi không chặn các cửa hàng còn lại */
    async settleBatch(rows) {
      this.isWorking = true;
      this.batchTargetCount = rows.length;
      this.batchDoneCount = 0;
      const period = {
        fromDate: this.toApiDate(this.filter.fromDate),
        toDate: this.toApiDate(this.filter.toDate),
      };
      let failed = 0;
      ApiService.setHeader();
      for (const row of rows) {
        row.settling = true;
        row.error = '';
        try {
          const { data } = await ApiService.post('commission-settlements', {
            type: 'store',
            storeId: row.storeId,
            ...period,
          });
          const result = data.data || {};
          row.done = true;
          row.isSettled = true;
          row.settlementId = result.id;
          row.settledTotal = result.totalAmount;
          row.totalAmount = result.totalAmount;
          row.employeeCount = result.employeeCount;
          row.diff = 0;
          row.checked = false;
        } catch (error) {
          failed += 1;
          row.error = this.getErrorMessage(error, 'Chốt sổ thất bại');
        }
        row.settling = false;
        this.batchDoneCount += 1;
      }
      this.isWorking = false;
      this.fetchHistory();
      if (failed) {
        makeToastFaile(`Chốt xong ${rows.length - failed}/${rows.length} cửa hàng, ${failed} lỗi.`);
      } else {
        makeToastSuccess(`Đã chốt sổ ${rows.length} cửa hàng.`);
      }
    },

    /** Số liệu sẽ chốt: gọi đúng báo cáo tổng hợp mà API chốt sổ dùng để lưu */
    fetchPreview() {
      this.previewLoading = true;
      ApiService.setHeader();
      ApiService.query('commission-reports', { params: this.buildParams() })
        .then(({ data }) => {
          const result = data.data || {};
          this.preview = { items: result.items || [], totals: result.totals || {} };
        })
        .catch((error) => {
          this.preview = null;
          makeToastFaile(
            this.getErrorMessage(error, 'Không tải được số liệu xem trước'),
          );
        })
        .finally(() => {
          this.previewLoading = false;
        });
    },

    confirmSettle() {
      const isResettle = this.status && this.status.isSettled;
      Swal.fire({
        title: isResettle ? 'Chốt lại kỳ này?' : 'Chốt sổ kỳ này?',
        text: isResettle
          ? 'Bản chốt cũ sẽ bị GHI ĐÈ hoàn toàn bằng số liệu mới nhất.'
          : 'Sau khi chốt, bút toán cộng/trừ thuộc kỳ sẽ bị khoá.',
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#3699FF',
        cancelButtonColor: '#E5EAEE',
        confirmButtonText: isResettle ? 'Chốt lại' : 'Chốt sổ',
        cancelButtonText: 'Huỷ',
      }).then((result) => {
        if (result.value) {
          this.settle();
        }
      });
    },
    settle() {
      this.isWorking = true;
      ApiService.setHeader();
      ApiService.post('commission-settlements', this.buildParams())
        .then(({ data }) => {
          makeToastSuccess(data.message);
          this.checkStatus();
          this.fetchHistory();
        })
        .catch((error) => {
          makeToastFaile(
            this.getErrorMessage(error, 'Không chốt sổ được'),
          );
        })
        .finally(() => {
          this.isWorking = false;
        });
    },

    confirmRevoke(id) {
      Swal.fire({
        title: 'Huỷ chốt kỳ này?',
        text: 'Kỳ sẽ được mở khoá, bút toán cộng/trừ sửa được trở lại.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: 'rgb(221, 51, 51)',
        cancelButtonColor: '#E5EAEE',
        confirmButtonText: 'Huỷ chốt',
        cancelButtonText: 'Đóng',
      }).then((result) => {
        if (result.value) {
          this.revoke(id);
        }
      });
    },
    revoke(id) {
      this.isWorking = true;
      ApiService.setHeader();
      ApiService.delete(`commission-settlements/${id}`)
        .then(({ data }) => {
          makeToastSuccess(data.message);
          this.checkStatus();
          this.fetchHistory();
        })
        .catch((error) => {
          makeToastFaile(
            this.getErrorMessage(error, 'Không huỷ chốt được'),
          );
        })
        .finally(() => {
          this.isWorking = false;
        });
    },

    fetchHistory() {
      this.onLoadingHistory = true;
      ApiService.setHeader();
      ApiService.query('commission-settlements', { params: { limit: 50 } })
        .then(({ data }) => {
          this.history = data.data.items || [];
        })
        .catch(() => {
          this.history = [];
        })
        .finally(() => {
          this.onLoadingHistory = false;
        });
    },

    openStatement(id) {
      this.statement = null;
      this.statementVisible = true;
      this.statementLoading = true;
      ApiService.setHeader();
      ApiService.query(`commission-settlements/${id}/statement`)
        .then(({ data }) => {
          this.statement = data.data;
        })
        .catch((error) => {
          makeToastFaile(
            this.getErrorMessage(error, 'Không tải được bảng kê'),
          );
        })
        .finally(() => {
          this.statementLoading = false;
        });
    },
  },
};
</script>

<style scoped>
.form-label {
  display: block;
  font-size: 12px;
  color: #7e8299;
  margin-bottom: 4px;
}
.hint {
  font-size: 13px;
  border: 1px dashed #d6d9e0;
}
.settlement-filter {
  background: #f8f9fb;
  border: 1px solid #ecf0f3;
  border-radius: 6px;
  padding: 16px 20px;
}
/* Nút dạng link của theme chỉ lên màu khi hover — ép luôn xanh để nhìn ra là bấm được */
.settlement-link,
.settlement-link:focus {
  color: #3699ff !important;
  font-weight: 500;
}
.settlement-link:hover {
  color: #187de4 !important;
  text-decoration: underline;
}
.status-box {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  border: 1px solid #ebedf3;
  border-radius: 6px;
  padding: 16px;
}
.status-box--locked {
  background: #fff4de;
  border-color: #ffe2a8;
}
.status-box--open {
  background: #f1faff;
  border-color: #cfe7fb;
}
.status-box__title {
  font-size: 15px;
  font-weight: 600;
  color: #181c32;
}
.status-box__meta {
  font-size: 13px;
  color: #7e8299;
  margin-top: 4px;
}
.statement-head {
  background: #f8f9fb;
  border: 1px solid #ebedf3;
  border-radius: 6px;
  padding: 12px 16px;
  font-size: 13px;
  line-height: 1.9;
}
.statement-total td {
  background: #f3f6f9;
  font-weight: 700;
  color: #181c32;
}
</style>
